/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Credentials } from '../models/credentials.interface';
import { environment } from '../../../environments/environment';
import { Session, SessionTokens, User } from '@shared/models';
import { PersistenceService } from '@migo-apps/utils';
import {
  tap,
  of,
  map,
  switchMap,
  timer,
  Subscription,
  retry,
  Observable,
} from 'rxjs';
import { setMillisecondsToDate } from '@shared/utils';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

const generateEndpoint = environment.generateEndpoint;
const authHeaderName = environment.authHeaderName;
const appKey = environment.appKey;

/**
 *Servicio de autenticacion
 *
 * @export
 * @class AuthenticationService
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private subcription!: Subscription;

  constructor(
    private http: HttpClient,
    private persistenceSvc: PersistenceService,
    private router: Router,
    private snackbar: MatSnackBar
  ) {}

  /**
   *Metodo para realizar un inicio de sesion
   *
   * @param {Credentials} credentials Credenciales del usuario
   * @return {Observable<Session>}
   * @memberof AuthenticationService
   */
  login(credentials: Credentials, isRedirect = false): Observable<Session> {
    return this.http
      .post<Session>(
        generateEndpoint(['auth', 'login']),
        {
          email: credentials.email,
          password: credentials.password,
        },
        { headers: { [authHeaderName]: appKey } }
      )
      .pipe(
        tap(() => {
          /**
           * Se almacena o se borra el email de las credenciales de acuerdo
           * a la propiedad "remember"
           */
          if (credentials.remember) {
            this.persistenceSvc.local.setItem('user-email', credentials.email);
          } else {
            this.persistenceSvc.local.removeItem('user-email');
          }
        }),
        switchMap((session) =>
          isRedirect ? of(session) : this.setSession(session)
        )
      );
  }

  /**
   *Destruye la sesion actual y elimina la subscripcion encargada de refrescar
   *dicha sesion
   * @return {Observable<void>}
   * @memberof AuthenticationService
   */
  logout(): Observable<void> {
    this.subcription?.unsubscribe();
    return of(this.persistenceSvc.session.clear());
  }

  /**
   *Valida si la sesion actual es valida
   *
   * @return {Observable<boolean>}
   * @memberof AuthenticationService
   */
  isLogged(): Observable<boolean> {
    return this.getSession().pipe(
      map(
        (session) =>
          (!!session?.sessionToken || !!session?.authToken) &&
          new Date(session?.expireDate).getTime() > new Date().getTime()
      )
    );
  }

  /**
   *Retorna el usuario actual
   *
   * @return {*}  {Observable<User>}
   * @memberof AuthenticationService
   */
  getUser$(): Observable<User> {
    return this.getSession().pipe(map((session) => session.user));
  }

  getUser(): User {
    const sesion = this.persistenceSvc.session.getItem<Session>(
      'session'
    ) as Session;
    return sesion.user;
  }

  /**
   *Retorna la sesion actual
   *
   * @return {*}
   * @memberof AuthenticationService
   */
  getSession() {
    return of(
      this.persistenceSvc.session.getItem<Session>('session') as Session
    );
  }

  /**
   *Retorna el email recordado si existiere
   *
   * @return {*}
   * @memberof AuthenticationService
   */
  getRememberedEmail() {
    return this.persistenceSvc.local.getItem<string>('user-email') as string;
  }

  setPermissions(permissions: string) {
    return this.persistenceSvc.local.setItem('permissions', permissions);
  }

  removePermissions() {
    return this.persistenceSvc.local.removeItem('permissions');
  }

  getPermissions() {
    return this.persistenceSvc.local.getItem<string>('permissions') as string;
  }

  /**
   *Realiza la solicitud para un reestablecimiento de contraseña
   *
   * @param {string} email
   * @return {*}
   * @memberof AuthenticationService
   */
  sendPasswordResetRequest(email: string) {
    return this.http
      .post(
        generateEndpoint(['auth', 'recover_password']),
        {
          email,
        },
        { headers: { [authHeaderName]: appKey } }
      )
      .pipe(tap(() => null));
  }

  /**
   *Envia la solicitud de validacion de un codigo otp
   *
   * @param {string} code
   * @param {string} email
   * @return {*}
   * @memberof AuthenticationService
   */
  validateOTP(code: string, email: string) {
    return this.http
      .post(
        generateEndpoint(['auth', 'validate_otp']),
        {
          code,
          email,
        },
        { headers: { [authHeaderName]: appKey } }
      )
      .pipe(tap(() => null));
  }

  /**
   *Envia la solicitud para establecer una nueva contraseña
   *
   * @param {string} email
   * @param {string} code
   * @param {string} password
   * @return {*}
   * @memberof AuthenticationService
   */
  resetPassword(email: string, code: string, password: string) {
    return this.http
      .post(
        generateEndpoint(['auth', 'change_password']),
        {
          email,
          code,
          password,
        },
        { headers: { [authHeaderName]: appKey } }
      )
      .pipe(tap(() => null));
  }

  /**
   * It takes a password, gets the session, then sends the password to the server to
   * activate the account
   * @param {string} password - string
   * @returns The return value is an observable of the http response.
   */
  activateAccount(password: string) {
    return this.getSession()
      .pipe(
        switchMap((session) =>
          this.http.post(
            generateEndpoint(['auth', 'activate_account']),
            {
              password,
            },
            { headers: { [authHeaderName]: 'Bearer ' + session.authToken } }
          )
        )
      )
      .pipe(tap(() => null));
  }

  /**
   * If the user is active and has a session token, refresh the token
   * @returns A boolean value.
   */
  activateTokenRefresh() {
    return this.getSession().pipe(
      tap((session) => {
        if (
          session &&
          session.user.status === 'active' &&
          session.sessionToken
        ) {
          this.refreshToken();
        }
      })
    );
  }

  /**
   *Permite refrescar la sesion actual, se ejecuta un minuto antes de la expiracion
   *de dicha sesion
   * @private
   * @memberof AuthenticationService
   */
  private refreshToken() {
    let session: Session;

    if (this.subcription) {
      this.subcription.unsubscribe();
    }

    this.subcription = this.getSession()
      .pipe(
        map((s) => {
          session = s;
          return new Date(session.expireDate);
        }),
        map((date) => new Date(date.setMinutes(date.getMinutes() - 1))),
        switchMap((exp) => timer(exp)),
        switchMap(() =>
          this.http.post<SessionTokens>(
            generateEndpoint(['auth', 'refresh']),
            {
              refreshToken: session.refreshToken,
            },
            { headers: { [authHeaderName]: 'Bearer ' + session.sessionToken } }
          )
        ),
        retry(3),
        map((tokens) => ({ ...session, ...tokens } as Session))
      )
      .subscribe({
        next: (newSession) => {
          this.setSession(newSession).subscribe(() => {
            this.refreshToken();
          });
        },
        error: () => {
          this.logout();
          this.router.navigate(['/']);
          this.snackbar.open('La sesion ha caducado', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        },
      });
  }

  /**
   *Almacena localmente la nueva sesion activa
   *
   * @private
   * @param {Session} session
   * @memberof AuthenticationService
   */
  private setSession(session: Session) {
    return of(session).pipe(
      map((s) => {
        s.expireDate = setMillisecondsToDate(session.expireTime);
        this.persistenceSvc.session.setItem('session', session);
        this.persistenceSvc.session.setItem('imgUrl', session.user.avatarUrl);
        return session;
      })
    );
  }

  setSessionPublic(session: Session) {
    session.expireDate = setMillisecondsToDate(session.expireTime);
    this.persistenceSvc.session.setItem('session', session);
    this.persistenceSvc.session.setItem('imgUrl', session.user.avatarUrl);
  }
}
